import { apiRequest, USE_MOCKS } from './client'
import {
  mockAckNodeModelSlot,
  mockAddNodeAvailableModel,
  mockCreateNodeModelDeployment,
  mockDeleteNodeModelMapping,
  mockGetModelArtifactByRelease,
  mockNodeAvailableModels,
  mockNodeModelStates,
  mockRefreshModelStatus,
  mockRemoveNodeAvailableModel,
  mockUpsertNodeModelMapping,
} from '@/mocks/models'
import type {
  CreateCommandResponse,
  CreateModelDeploymentResponse,
  ModelArtifactByReleaseResponse,
  ModelCameraMappingSummary,
  ModelStateItem,
  ModelStatesResponse,
  NodeAvailableModelsResponse,
} from '@/types'

export async function listNodeModelStates(nodeId: string): Promise<ModelStatesResponse> {
  if (USE_MOCKS) return mockNodeModelStates(nodeId)
  return apiRequest<ModelStatesResponse>(`/api/nodes/${nodeId}/model-states`)
}

export async function listNodeAvailableModels(nodeId: string): Promise<NodeAvailableModelsResponse> {
  if (USE_MOCKS) return mockNodeAvailableModels(nodeId)
  return apiRequest<NodeAvailableModelsResponse>(`/api/nodes/${nodeId}/available-models`)
}

export async function getModelArtifactByRelease(
  modelKey: string,
  version: string,
): Promise<ModelArtifactByReleaseResponse> {
  if (USE_MOCKS) return mockGetModelArtifactByRelease(modelKey, version)
  return apiRequest<ModelArtifactByReleaseResponse>(
    `/api/model-artifacts/by-release/${encodeURIComponent(modelKey)}/${encodeURIComponent(version)}`,
  )
}

export async function addNodeAvailableModel(
  nodeId: string,
  modelArtifactId: string,
): Promise<void> {
  if (USE_MOCKS) return mockAddNodeAvailableModel(nodeId, modelArtifactId)
  await apiRequest<void>(`/api/nodes/${nodeId}/available-models/${modelArtifactId}`, {
    method: 'PUT',
  })
}

export async function removeNodeAvailableModel(
  nodeId: string,
  modelArtifactId: string,
): Promise<void> {
  if (USE_MOCKS) return mockRemoveNodeAvailableModel(nodeId, modelArtifactId)
  await apiRequest<void>(`/api/nodes/${nodeId}/available-models/${modelArtifactId}`, {
    method: 'DELETE',
  })
}

export async function materializeNodeModelCameras(nodeId: string): Promise<void> {
  if (USE_MOCKS) return
  await apiRequest(`/api/nodes/${nodeId}/model-cameras/materialize`, { method: 'POST' })
}

export async function upsertNodeModelMapping(
  nodeId: string,
  cameraKey: string,
  modelArtifactId: string,
): Promise<ModelCameraMappingSummary> {
  if (USE_MOCKS) return mockUpsertNodeModelMapping(nodeId, cameraKey, modelArtifactId)
  return apiRequest<ModelCameraMappingSummary>(`/api/nodes/${nodeId}/model-mappings`, {
    method: 'PUT',
    body: JSON.stringify({
      camera_key: cameraKey,
      model_artifact_id: modelArtifactId,
      mapping_source: 'manual',
    }),
  })
}

export async function deleteNodeModelMapping(nodeId: string, cameraKey: string): Promise<void> {
  if (USE_MOCKS) return mockDeleteNodeModelMapping(nodeId, cameraKey)
  await apiRequest(`/api/nodes/${nodeId}/model-mappings/${encodeURIComponent(cameraKey)}`, {
    method: 'DELETE',
  })
}

export async function createNodeModelDeployment(
  nodeId: string,
  cameraKey: string,
  reason?: string,
): Promise<CreateModelDeploymentResponse> {
  if (USE_MOCKS) return mockCreateNodeModelDeployment(nodeId, cameraKey, reason)
  return apiRequest<CreateModelDeploymentResponse>(`/api/nodes/${nodeId}/model-deployments`, {
    method: 'POST',
    body: JSON.stringify({ camera_key: cameraKey, reason }),
  })
}

export async function ackNodeModelSlot(nodeId: string, slotKey: string): Promise<ModelStateItem> {
  if (USE_MOCKS) return mockAckNodeModelSlot(nodeId)
  return apiRequest<ModelStateItem>(`/api/nodes/${nodeId}/model-slots/${encodeURIComponent(slotKey)}/ack`, {
    method: 'POST',
    body: JSON.stringify({}),
  })
}

export async function refreshModelStatus(nodeId: string, slotKey?: string | null): Promise<CreateCommandResponse> {
  if (USE_MOCKS) return mockRefreshModelStatus(nodeId)
  return apiRequest<CreateCommandResponse>(`/api/nodes/${nodeId}/commands`, {
    method: 'POST',
    body: JSON.stringify({
      type: 'model.status.fetch',
      params: { node_id: nodeId, slot_key: slotKey ?? undefined },
      reason: 'model readiness refresh',
      idempotency_key: `model-status-fetch:${nodeId}:${slotKey ?? 'all'}:${Date.now()}`,
    }),
  })
}
